import React from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import './NotificationMatrixHelpModal.css';

/**
 * Help for System Controls → Notification matrix.
 * Explains org toggles (channels OPEN) vs fixed persona routing (not editable here).
 * Persona table mirrors docs/NOTIFICATION_CHANNEL_MATRIX.md Target matrix.
 */

const PERSONA_COLUMNS = [
  'Superadmin',
  'Admin',
  'Manager',
  'Help Desk',
  'Coordinator',
  'Custodian',
  'Technician',
  'Reporter / Subject',
];

/** [domain, functionality, ...persona cells matching PERSONA_COLUMNS] */
const PERSONA_ROWS = [
  ['Auth', 'Account verification', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'Temporary password', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'Welcome email', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'Email verification confirmed', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'User approved', '—', '—', '—', '—', '—', '—', '—', 'W'],
  ['Auth', 'WhatsApp number verification', '—', '—', '—', '—', '—', '—', '—', 'W'],
  ['Auth', 'Password reset + success', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'Email / username changed', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Auth', 'Account reactivation link', '—', '—', '—', '—', '—', '—', '—', 'E'],
  ['Approvals', 'Device submitted / resubmission', 'A', 'E · W · A', '—', '—', '—', '—', '—', '—'],
  ['Approvals', 'Manager submission confirmation', '—', '—', 'E · A', '—', '—', '—', '—', '—'],
  ['Approvals', 'Device approved', '—', '—', 'E · A', '—', '—', '—', '—', '—'],
  ['Approvals', 'Device rejected', '—', '—', 'E · W · A', '—', '—', '—', '—', '—'],
  ['Transfers', 'Initiation / pending source', '—', 'E · A', 'E · A', '—', '—', 'E · A', '—', '—'],
  ['Transfers', 'Incoming (destination accept)', '—', 'E · A', 'E · A', '—', '—', 'E · A', 'E · A†', '—'],
  ['Transfers', 'Source approved', '—', '—', 'E · A', '—', '—', 'E · A', 'E · A', '—'],
  ['Transfers', 'Completed (origin)', '—', 'E · A', 'E · A', '—', '—', '—', 'E · A‡', '—'],
  ['Transfers', 'Rejected / cancelled', '—', '—', 'E · A', '—', '—', '—', 'E · A', '—'],
  ['Dept transfers', 'Pending (destination)', '—', 'E · A', 'E · A', '—', '—', 'E · A', '—', '—'],
  ['Dept transfers', 'Completed / rejected / cancelled', '—', 'E · A', 'E · A', '—', '—', 'E · A', '—', '—'],
  ['Transfers', 'Technician department update', '—', '—', 'E · A', '—', '—', 'A', 'E · W · A', '—'],
  ['Complaints', 'Created', '—', '—', 'E · A', 'A', '—', 'E · A', 'E · W · A', 'E · W · A§'],
  ['Complaints', 'Assigned (new technician)', '—', '—', '—', '—', '—', '—', 'E · W · A', '—'],
  ['Complaints', 'Unassigned (previous technician)', '—', '—', '—', '—', '—', '—', 'W · A', '—'],
  ['Complaints', 'Status update', '—', '—', 'A', 'A', '—', 'A', 'A*', 'W · A¶'],
  ['Complaints', 'Availability impact', '—', '—', 'E · W · A', '—', '—', 'E · W · A', '—', 'W · A¶'],
  ['Complaints', 'Severity update', '—', '—', 'A', '—', '—', '—', '—', 'W · A¶'],
  ['Complaints', 'ACK / closure TAT breach', 'A', 'A', 'E · W · A', 'A', 'A', 'A', 'E · W · A', '—'],
  ['Scan alerts', 'PPM / Cal / Compliance (digest)', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', '—'],
  ['Scan alerts', 'Warranty / Contract (digest)', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', 'A · Eᵈ', '—'],
  ['Scan alerts', 'Budget overrun', 'A', 'E · W · A', 'E · W · A', '—', '—', '—', '—', '—'],
  ['Scan alerts', 'High-cost device pending approval', 'A', 'E · W · A', 'E · W · A', 'A', 'A', 'A', 'A', '—'],
];

function NotificationMatrixHelpModal({ isOpen, onClose }) {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = (
    <div className="notif-matrix-help-overlay" onClick={onClose}>
      <div
        className="notif-matrix-help-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-matrix-help-title"
      >
        <div className="notif-matrix-help-header">
          <h2 id="notif-matrix-help-title">Notification channel matrix — help</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="notif-matrix-help-body">
          <section className="notif-matrix-help-section">
            <h3>What these toggles control</h3>
            <p>
              The table on System Controls turns notification <strong>channels open or closed</strong> for
              each event in this organization (Enabled, In-app, Email, WhatsApp).
            </p>
            <ul>
              <li>
                <strong>Channel ON</strong> means that channel may be used for the event — not that every
                role receives it.
              </li>
              <li>
                <strong>Who gets which channel</strong> is fixed by the persona mapping below (fatigue
                design). It is not editable in this screen.
              </li>
              <li>
                Example: Email ON for <code>complaint_availability_impact</code> still sends email only
                to Manager and Custodian — not to every persona (reporters get WhatsApp; auth also
                in-app).
              </li>
            </ul>
          </section>

          <section className="notif-matrix-help-section">
            <h3>Legend</h3>
            <table className="notif-matrix-help-table notif-matrix-help-legend">
              <thead>
                <tr>
                  <th>Mark</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>E</strong>
                  </td>
                  <td>Email</td>
                </tr>
                <tr>
                  <td>
                    <strong>W</strong>
                  </td>
                  <td>WhatsApp</td>
                </tr>
                <tr>
                  <td>
                    <strong>A</strong>
                  </td>
                  <td>In-app alert</td>
                </tr>
                <tr>
                  <td>
                    <strong>Eᵈ</strong>
                  </td>
                  <td>Email digest (batched)</td>
                </tr>
                <tr>
                  <td>
                    <strong>—</strong>
                  </td>
                  <td>Not notified on this flow</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="notif-matrix-help-section">
            <h3>Persona mapping (fixed)</h3>
            <p className="notif-matrix-help-note">
              Self-notify is suppressed for the actor who performed the action, except subject/receipt
              flows (auth, reporter updates, manager submission confirmation).
            </p>
            <div className="notif-matrix-help-table-scroll">
              <table className="notif-matrix-help-table notif-matrix-help-persona">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Event</th>
                    {PERSONA_COLUMNS.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERSONA_ROWS.map((row) => {
                    const [domain, event, ...cells] = row;
                    return (
                      <tr key={`${domain}-${event}`}>
                        <td>{domain}</td>
                        <td>{event}</td>
                        {cells.map((cell, i) => (
                          <td key={PERSONA_COLUMNS[i]} className={cell === '—' ? 'is-empty' : ''}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="notif-matrix-help-section">
            <h3>Footnotes</h3>
            <table className="notif-matrix-help-table notif-matrix-help-legend">
              <thead>
                <tr>
                  <th>Mark</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>*</strong>
                  </td>
                  <td>
                    Technician in-app on status only when closed/cancelled by someone else; never WhatsApp
                    on status.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>†</strong>
                  </td>
                  <td>Incoming technicians: device/department-scoped only.</td>
                </tr>
                <tr>
                  <td>
                    <strong>‡</strong>
                  </td>
                  <td>Origin complete: initiating technician (E · A) when initiator is a technician.</td>
                </tr>
                <tr>
                  <td>
                    <strong>§</strong>
                  </td>
                  <td>
                    Reporter on create: guest <strong>E · W</strong>; authenticated <strong>E · W · A</strong>
                    .
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>¶</strong>
                  </td>
                  <td>
                    Status / severity / availability reporter: authenticated <strong>W · A</strong>; guest{' '}
                    <strong>W</strong> only (same as auth, minus in-app).
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default NotificationMatrixHelpModal;
